/**
 * Enable Banking — OUR wire contract for `/api/v1/enable-banking/*`.
 *
 * Two layers meet in this module and the split is deliberate:
 *
 *   ./enableBanking.generated.ts  Enable Banking's OWN schemas, generated from
 *                                 their published OpenAPI 3.1 document and pinned.
 *                                 Never hand-edit; regenerate with
 *                                 `packages/shared/scripts/generate-enable-banking-schemas.mjs`.
 *   this file                     the request/response shapes exchanged between
 *                                 apps/web and apps/api — hand-written, because
 *                                 they are ours and no spec describes them.
 *
 * The generated module is re-exported from here so consumers have ONE import
 * surface (`@releve/shared`) and never have to know which half a symbol came from.
 *
 * WHAT DELIBERATELY NEVER APPEARS ON THIS WIRE:
 *  - `session_id`. It is the only secret we store and it stays server-side.
 *  - `account_uid`. Session-scoped, changes on every reconnect, and useless to the
 *    browser; `is_in_current_consent` carries the one bit the UI actually needs.
 *  - `identification_hash`. The durable account-matching key, and personal data
 *    where the account holder is a sole trader. The UI identifies accounts by our
 *    own row id.
 *
 * Reads are RLS-scoped (`SECURITY INVOKER`, org policies); writes go through the
 * RESTRICTIVE `verify_backend_token()` policies on the two tables — Pattern B+ per
 * docs/BACKEND_API.md. No SECURITY DEFINER function is involved.
 */
import { z } from "zod";
import { ebPSUTypeSchema, } from "./enableBanking.generated.js";
export * from "./enableBanking.generated.js";
/* ------------------------------------------------------------------------- *
 * Shared vocabulary
 * ------------------------------------------------------------------------- */
/**
 * Lifecycle of one bank connection (`bank_connections.status`).
 *
 * These five values MUST stay in step with the `bank_connections_status_check`
 * CHECK constraint in the migration — note it spells the live state `authorized`,
 * not `active`.
 *
 * `expiring soon` is NOT a status: it is derived in the UI from `valid_until`
 * against the org timezone (`dbCalendarDayInOrgZone`), never from a stored flag
 * that would go stale the moment nobody looks at it. `error` means the last sync
 * failed while the consent itself is still believed good — the details are in
 * `last_error`.
 */
export const bankConnectionStatusSchema = z.enum([
    /** Authorization started, the PSU has not come back through the callback yet. */
    "pending",
    /** Authorized and inside `valid_until`. */
    "authorized",
    /** Consent lapsed. An ORDINARY state with a Reconnect path, not a failure. */
    "expired",
    /** Consent withdrawn — by the PSU at the bank, or by us via disconnect. */
    "revoked",
    /** Last operation failed; see `last_error`. */
    "error",
]);
/**
 * The five outbound Enable Banking calls recorded in
 * `bank_connection_activity_audit` (Pillar 3, docs/AUDIT.md) — one audit row per
 * call, success or failure.
 *
 * These values MUST stay in step with the `action` CHECK constraint on that
 * table: the constant lives here, in the one module both apps import, so the
 * route that writes a verb and the constraint that accepts it cannot drift.
 * Nothing on the wire carries them today — the audit trail is read in SQL until
 * a viewer is designed.
 */
export const BANK_CONNECTION_ACTIVITY_ACTIONS = [
    /** `POST connections/authorize` → the bank's authorization URL. */
    "AUTHORIZE",
    /** `POST connections/callback` → `POST /sessions` (the consent itself). */
    "SESSION_CREATE",
    /** `DELETE /sessions/{id}` — from disconnect, or cleaning up an orphan. */
    "SESSION_DELETE",
    /** One sync click's whole transaction walk, however many pages it took. */
    "FETCH_TRANSACTIONS",
    /** `GET /accounts/{uid}/balances` on the same click. */
    "FETCH_BALANCES",
];
/* ------------------------------------------------------------------------- *
 * 1. GET /orgs/:orgId/enable-banking/aspsps
 * ------------------------------------------------------------------------- */
/**
 * `country` is REQUIRED, not defaulted server-side: `organizations.country` is
 * nullable and unset for most orgs, and an NL org connecting a Belgian account is
 * a normal case. The route additionally pins `service=AIS` so the list only offers
 * banks that support what we are registered to do.
 */
export const listAspspsQuerySchema = z
    .object({
    country: z
        .string()
        .regex(/^[A-Z]{2}$/, { message: "Expected a two-letter ISO 3166 country code" }),
})
    .strict();
/* ------------------------------------------------------------------------- *
 * 2. POST /orgs/:orgId/enable-banking/connections/authorize
 * ------------------------------------------------------------------------- */
/**
 * A path inside THIS org that the callback page returns the user to. Two
 * surfaces host the feed tab (`/accounting/bank-accounts` and
 * `/accounting/credit-cards`, both of them `AccountFeedWorkspace`), both
 * bookmarkable via `?account=` / `?tab=`, so the query string is part of the
 * value and a credit-card connection must come back to the credit-cards page.
 *
 * Shape-checked here as an open-redirect guard; the ROUTE must additionally assert
 * the embedded org id matches `:orgId`, and the callback page must re-assert the
 * prefix before navigating. A schema cannot see the org id, so this is one half of
 * a three-part check, never the whole of it.
 */
export const orgRelativeReturnPathSchema = z
    .string()
    .min(1)
    .max(2048)
    .refine((value) => value.startsWith("/org/"), {
    message: "return_to must be an /org/ path",
})
    .refine((value) => !value.startsWith("//") && !value.includes("\\"), {
    message: "return_to must not be protocol-relative",
});
/**
 * Start an authorization. `required_psu_headers` is deliberately NOT accepted from
 * the browser even though the picker has it: the route re-resolves the bank
 * against `GET /aspsps` anyway (a rebrand changes the name and fails with
 * `WRONG_ASPSP_PROVIDED`), so it takes the header list from that same response
 * rather than trusting a client-supplied one.
 *
 * `psu_type` is technically optional on Enable Banking's `POST /auth`, but its
 * default depends on the connector and a mismatch either fails the authorization
 * or hides the accounts the user expects — an expensive thing to debug from a
 * support ticket. Required here.
 */
export const startBankConnectionSchema = z
    .object({
    aspsp_name: z.string().min(1).max(255),
    aspsp_country: z.string().regex(/^[A-Z]{2}$/),
    psu_type: ebPSUTypeSchema,
    return_to: orgRelativeReturnPathSchema,
    /** Present ⇒ this is a RECONNECT of an existing connection, not a new one. */
    connection_id: z.string().uuid().nullable().optional(),
})
    .strict();
/* ------------------------------------------------------------------------- *
 * 3. POST /orgs/:orgId/enable-banking/connections/callback
 * ------------------------------------------------------------------------- */
/**
 * Completes an authorization. `state` is our own stateless signed nonce
 * (`base64url(payload) + "." + HMAC`), not an opaque server-side handle: the
 * callback page is org-less and must decode it to learn which org to post to,
 * because `requireOrgMember()` resolves the org only from a header, a `:orgId`
 * param or `organization_id` in the body.
 *
 * The server verifies the HMAC, that the payload's org matches `:orgId`, that its
 * user matches the caller, and that `exp` has not passed. Replay is covered
 * separately because Enable Banking's `code` is itself single-use.
 */
export const bankConnectionCallbackSchema = z
    .object({
    code: z.string().min(1).max(4096),
    state: z.string().min(1).max(4096),
})
    .strict();
/* ------------------------------------------------------------------------- *
 * 5. POST /orgs/:orgId/enable-banking/connections/:connectionId/disconnect
 * ------------------------------------------------------------------------- */
/**
 * No fields — the connection is the `:connectionId` param. Present as a schema so
 * the route validates with the same apparatus as its siblings and an unknown key
 * is a clean 400. The whole body may be absent.
 */
export const disconnectBankConnectionSchema = z.object({}).strict();
/* ------------------------------------------------------------------------- *
 * 6. POST /orgs/:orgId/enable-banking/connection-accounts/:accountId/link
 * ------------------------------------------------------------------------- */
/**
 * Link (or UNLINK, with `chart_of_accounts_id: null`) one bank account to one
 * ledger account.
 *
 * The route enforces the guards a schema cannot see: the ledger account is a feed
 * type (`isFeedAccountType`), currencies match — treating `XXX` as "matches
 * anything", since a multi-currency account never equals a ledger currency and the
 * plain equality check would otherwise reject every one of them permanently — and
 * neither side is already linked (a partial unique index on `chart_of_accounts_id`
 * backs that up in the database).
 *
 * UNLINK WARNING, owed by the UI: re-linking to a DIFFERENT ledger account
 * re-imports history, because the dedup index is scoped by `bank_account_id`.
 * Imported rows are left untouched.
 */
export const linkBankConnectionAccountSchema = z
    .object({
    chart_of_accounts_id: z.string().uuid().nullable(),
    /** Only meaningful for a credit-card target; ignored on unlink. */
    flip_card_sign: z.boolean().optional(),
})
    .strict();
/* ------------------------------------------------------------------------- *
 * 7. POST /orgs/:orgId/enable-banking/connection-accounts/:accountId/sync
 * ------------------------------------------------------------------------- */
/**
 * No fields. The first sync of an account asks the bank for EVERYTHING it makes
 * available (no date filter, no count limit — ruled 2026-09-06); every later sync
 * is incremental from `last_synced_at` minus a 14-day overlap. `date_to` is never
 * sent: apps/api has no org-timezone helper, so a serverless function's "today" is
 * UTC and an org just after midnight would silently lose its last day.
 */
export const syncBankConnectionAccountSchema = z.object({}).strict();
//# sourceMappingURL=enableBanking.js.map